<template>
  <div
      class="editable-element-drag-drop"
      :class="{ 'lock': elementInfo.lock }"
      :style="{
      width: '100%',
    }"
  >
    <div
        :class="cssClasses"
        ref="elementRef"
        :style="{
          width: '100%',
          height: elementInfo.vertical ? elementInfo.height + 'px' : '100%',
          backgroundColor: elementInfo.fill,
          opacity: elementInfo.opacity,
          textShadow: shadowStyle,
          lineHeight: elementInfo.lineHeight,
          letterSpacing: (elementInfo.wordSpace || 0) + 'px',
          color: elementInfo.defaultColor,

        }"
        @mousedown="determineMoveCapability($event)"
        @touchstart="determineMoveCapability($event)"
        @click="widgetStore.setActiveDragDrop(elementInfo.id)"
    >
      <div v-for="row in chosenLayout" :key="row.id" class="row draggable-row">
        <div
            v-for="child in row.children"
            :key="child.id"
            :class="{
                    'col': true,
                    'subsection': selectedSubsectionRowId == row.id && toolbarState === 'section',
                    'active': selectedSubsectionId == child.id && subsectionSelected
                  }"
            :style="getContainerParentStyle(child)"
            @click="setActiveSubsection(row.id, child.id); onSubSectionSelected($event);"
        >
          <div :style="getParentStyle(child)" :class="{ [child.widgetClass]: child.widgetClass }">

            <Draggable
                :id="`${elementInfo.id}-${child.id}`"
                :list="getDropZone(`${elementInfo.id}-${child.id}`)"
                :class="{
                    'widget-group': true,
                    'widget-group-highlight': selectedWidgetGroupId === `${elementInfo.id}-${child.id}`,
                    'grey-background': getDropZone(`${elementInfo.id}-${child.id}`).length === 0
                  }"

                group="elements"
                itemKey="name"
                @end="moveWidget"
                :disabled="disableDrag"
                :style="getSubsectionAlignment(child)"
            >
              <template #item="element">
                <div :style="getDimensionStyle(element.element.data, child)"
                     :key="uuid.v4() + recalculate"
                     @click="selectWidgetElement($event, element.element.data, `${elementInfo.id}-${child.id}`); setActiveSubsection(row.id, child.id);"
                     :class="{
                          'focus-style': highlightItem(element.element.data),
                          'prototype-tile': element.element.data.prototypeTile
                        }"
                         class="item-container">

                      <AiChatButton v-if="element.element.data.aiButton" :element-data="element.element.data" />

                      <div class="widget-holder"
                           :id="`${element.element.data.id}`"
                           :style="getStyle(element.element.data, child)"
                           :class="{
                              [element.element.data.widgetClass]: element.element.data.widgetClass
                            }"
                  >

                    <component
                        :elementData="element.element.data"
                        :is="element.element.component"
                        :style="{ width: getStyle(element.element.data, child).justifyContent === 'space-between' ? '100%' : ''}"
                    />
                  </div>
                </div>
              </template>
            </Draggable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, markRaw, onBeforeMount, onMounted, PropType, ref, watch} from 'vue'
import {ChartOptions, DraggableElement, PPTDragDropElement} from '@/types/slides'
import {useMainStore, useSlidesStore} from '@/store'
import 'bootstrap/dist/css/bootstrap.css';
import Draggable from 'vuedraggable'
import CsvSelectorWidget from './components/CsvSelectorWidget.vue'
import {useCreateElement, useMicroservice} from '@/hooks/index'
import RegularTextWidget from './components/RegularTextWidget.vue'
import ImageWidget from "./components/ImageWidget.vue";
import ChartWidget from "../ChartElement/index.vue"
import TableWidget from "../TableElement/index.vue"
import ModelData from "./components/ModelDataWidget.vue"
import ButtonWidget from "@/views/components/element/DragDropElement/components/ButtonWidget.vue";
import { applyRules } from "@/utils/common";

import {storeToRefs} from 'pinia'
import {useWidgetStore} from '@/store/widgets'
import useElementShadow from '@/views/components/element/hooks/useElementShadow'
import useHistorySnapshot from "@/hooks/useHistorySnapshot";
import _ from "lodash";
import {uuid} from "vue-uuid";
import AiChatButton from "@/views/components/element/DragDropElement/components/AiChatButton.vue";


const api = useMicroservice()
const { addHistorySnapshot } = useHistorySnapshot()


const props = defineProps({
  elementInfo: {
    type: Object as PropType<PPTDragDropElement>,
    required: true,
  },
  selectElement: {
    type: Function as PropType<(e: MouseEvent | TouchEvent, element: PPTDragDropElement, canMove?: boolean) => void>,
    required: true,
  },
  sectionId: {
    type: String,
    required: true,
  },
  sectionRowId: {
    type: String,
    required: true,
  },
  sectionStyle: {
    type: Object
  },
  sectionUsePrototypeTile: {
    type: Boolean
  },
  sectionPrototypeTileStyle: {
    type: Object
  }
})

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  }
  else {
    return defaultValue
  }
}

const getAttrNumber = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? element?.style[all] : element?.style[which]
}

const getAttr = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
}

//Need null to return to know if we use baseStyles or overrideStyles in getStyle and getDimensionStyle
const getAttrStyle = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return null
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
};

const highlightItem = (data) => {
  if (!sectionSelected.value && !subsectionSelected.value) return selectedWidgets.value.includes(data)
}

const onSubSectionSelected = (event: any) => {
  if (event.target.classList.contains('subsection')) {
    sectionSelected.value = false
    subsectionSelected.value = true
  }
}

const getPaddingWidth = (element) => {
  return getAttrNumber(element, 'padding', 'paddingLeft') + getAttrNumber(element, 'padding', 'paddingRight') +
      (highlightItem(element) ? 5 : 0)
}

const getPaddingHeight = (element) => {
  return getAttrNumber(element, 'padding', 'paddingTop') + getAttrNumber(element, 'padding', 'paddingBottom') +
      (highlightItem(element) ? 5 : 0)
}

const getMarginHeight = (element) => {
  return getAttrNumber(element, 'margin', 'marginTop') + getAttrNumber(element, 'margin', 'marginBottom')
}

const getMarginWidth = (element) => {
  return getAttrNumber(element, 'margin', 'marginLeft') + getAttrNumber(element, 'margin', 'marginRight')
}

// For both width and height:
// The override hierarchy (from the highest priority to lowest):
// Individual widget styles, section all tiles settings, subsection all tiles settings, prototype tile styles
const computeWidgetDimensions = (styleSource, element, subsection, fallback: any = {}) => {
  const getWidth = () => {
    if (props.sectionStyle?.allTilesWidth) {
      return px(props.sectionStyle.allTilesWidth, null)
    }
    if (subsection?.style?.allTilesWidth) {
      return px(subsection.style.allTilesWidth, null)
    }
    const baseWidth = styleSource?.containerWidth || styleSource?.imageWidth
    if (baseWidth) {
      return `${baseWidth + getPaddingWidth(element)}px`
    }
    return typeof fallback.width === 'number'
        ? px(fallback.width, null)
        : fallback.width ?? `calc(100% - ${px(getMarginWidth(element), '0px')})`
  }

  const getHeight = () => {
    if (props.sectionStyle?.allTilesHeight) {
      return px(props.sectionStyle.allTilesHeight, null)
    }
    if (subsection?.style?.allTilesHeight) {
      return px(subsection.style.allTilesHeight, null)
    }
    const baseHeight = styleSource?.containerHeight || styleSource?.imageHeight
    if (baseHeight) {
      return `${baseHeight + getPaddingHeight(element)}px`
    }
    return typeof fallback.height === 'number'
        ? px(fallback.height, null)
        : fallback.height ?? `auto`
  }

  return {
    widgetWidth: getWidth(),
    widgetHeight: getHeight(),
  }
}

const getDimensionStyle = (element, subsection) => {
  // Styles from prototype tile
  let baseStyles: any = {}
  // Styles from individual widget, overrides the prototype tile styles for just this one widget
  let overrideStyles: any

  if (subsection.usePrototypeTile || props.sectionUsePrototypeTile) {
    const prototypeElement = {
      style: props.sectionPrototypeTileStyle ? props.sectionPrototypeTileStyle : subsection.prototypeTileStyle
    }

    const { widgetWidth, widgetHeight } = computeWidgetDimensions(prototypeElement.style, prototypeElement, subsection)

    baseStyles = {
      borderTop: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderTop')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderRight: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderRight')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderBottom: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderBottom')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderLeft: `${getAttrStyle(prototypeElement, 'borderWidth', 'borderLeft')} solid ${
          prototypeElement?.style?.borderColor
      }`,
      borderRadius: px(prototypeElement?.style?.borderRadius, '0'),
      backgroundColor: prototypeElement?.style?.backgroundColor,
      paddingTop: getAttrStyle(prototypeElement, 'padding', 'paddingTop'),
      paddingRight: getAttrStyle(prototypeElement, 'padding', 'paddingRight'),
      paddingBottom: getAttrStyle(prototypeElement, 'padding', 'paddingBottom'),
      paddingLeft: getAttrStyle(prototypeElement, 'padding', 'paddingLeft'),
      marginTop: getAttrStyle(prototypeElement, 'margin', 'marginTop'),
      marginRight: getAttrStyle(prototypeElement, 'margin', 'marginRight'),
      marginBottom: getAttrStyle(prototypeElement, 'margin', 'marginBottom'),
      marginLeft: getAttrStyle(prototypeElement, 'margin', 'marginLeft'),
      height: widgetHeight,
      width: widgetWidth
    }
  }

  const { widgetWidth, widgetHeight } = computeWidgetDimensions(element?.style, element, subsection, baseStyles)

  overrideStyles = {
    borderTop: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderTop') ?? baseStyles.borderTop?.split(' ')[0] ?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderTop,
    borderRight: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderRight') ?? baseStyles.borderRight?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderRight,
    borderBottom: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderBottom') ?? baseStyles.borderBottom?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderBottom,
    borderLeft: element?.style?.borderColor
        ? `${getAttrStyle(element, 'borderWidth', 'borderLeft') ?? baseStyles.borderLeft?.split(' ')[0]?? 0} solid ${
            element?.style?.borderColor
        }`
        : baseStyles.borderLeft,
    borderRadius: px(element?.style?.borderRadius, '0') || baseStyles.borderRadius,
    backgroundColor: element?.style?.backgroundColor || baseStyles.backgroundColor,
    paddingTop: getAttrStyle(element, 'padding', 'paddingTop') || baseStyles.paddingTop,
    paddingRight: getAttrStyle(element, 'padding', 'paddingRight') || baseStyles.paddingRight,
    paddingBottom: getAttrStyle(element, 'padding', 'paddingBottom') || baseStyles.paddingBottom,
    paddingLeft: getAttrStyle(element, 'padding', 'paddingLeft') || baseStyles.paddingLeft,
    marginTop: getAttrStyle(element, 'margin', 'marginTop') || baseStyles.marginTop,
    marginRight: getAttrStyle(element, 'margin', 'marginRight') || baseStyles.marginRight,
    marginBottom: getAttrStyle(element, 'margin', 'marginBottom') || baseStyles.marginBottom,
    marginLeft: getAttrStyle(element, 'margin', 'marginLeft') || baseStyles.marginLeft,
    height: widgetHeight,
    width: widgetWidth
  }

  const finalStyles = { ...baseStyles, ...overrideStyles }

  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value)
    return { ...finalStyles, ...results }
  }

  element.alternateColor = null

  return finalStyles
}

const getSubsectionAlignment = (element) => {
  // we have to have a column flex so things get flipped
  let justifyContent = element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align
  if (justifyContent === 'left') {
    justifyContent = 'start'
  }
  else if (justifyContent === 'right') {
    justifyContent = 'end'
  }

  return {
    justifyContent: element?.style?.valign,
    alignItems: justifyContent
  }
}

const getContainerParentStyle = (element) => {
  const style = {
    margin: 0,
    padding: 0,
    borderRadius: px(element?.style?.borderRadius, '0'),
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    height: px((element?.style?.containerHeight || element?.style?.imageHeight)
        + getAttrNumber(element, 'margin', 'marginTop') + getAttrNumber(element, 'margin', 'marginBottom'),'auto'),
    width: px(element?.style?.containerWidth || element?.style?.imageWidth,'auto'),
    backgroundColor: element?.style?.backgroundColor,
  }

  if (!element?.style?.containerWidth) {
    style.flexGrow = element['flex-grow'] || '1'
    style.flexShrink = 0
    style.flexBasis = 0
    style.display = 'flex'
    style.display = element['hidden'] ? 'none' : 'flex'
  }


  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value);
    return { ...style, ...results };
  } else {
    element.alternateColor = null;
  }

  return style
}

const getParentStyle = (element) => {
  const borderColor = element?.style?.borderColor

  return {
    marginTop: getAttr(element, 'margin', 'marginTop'),
    marginRight: getAttr(element, 'margin', 'marginRight'),
    marginBottom: getAttr(element, 'margin', 'marginBottom'),
    marginLeft: getAttr(element, 'margin', 'marginLeft'),
    paddingTop: getAttr(element, 'padding', 'paddingTop'),
    paddingRight: getAttr(element, 'padding', 'paddingRight'),
    paddingBottom: getAttr(element, 'padding', 'paddingBottom'),
    paddingLeft: getAttr(element, 'padding', 'paddingLeft'),
    display: 'flex',
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    borderTop: `${getAttr(element, 'borderWidth', 'borderTop')} solid ${borderColor}`,
    borderRight: `${getAttr(element, 'borderWidth', 'borderRight')} solid ${borderColor}`,
    borderBottom: `${getAttr(element, 'borderWidth', 'borderBottom')} solid ${borderColor}`,
    borderLeft: `${getAttr(element, 'borderWidth', 'borderLeft')} solid ${borderColor}`,
    borderRadius: px(element?.style?.borderRadius, '0'),
    backgroundColor: element?.style?.backgroundColor,
    height: px(element?.style?.containerHeight || element?.style?.imageHeight,'auto'),
    width: px(element?.style?.containerWidth || element?.style?.imageWidth,'auto'),
  }
}

const getStyle = (element: any, subsection: any) => {
  let baseStyles: any = {}
  let overrideStyles: any

  if (subsection.usePrototypeTile || props.sectionUsePrototypeTile) {
    const prototypeElement = {
      style: props.sectionPrototypeTileStyle ? props.sectionPrototypeTileStyle : subsection.prototypeTileStyle
    }

    // For any prosemirror styles, no need to add to override styles
    // because prosemirror will override them automatically by adding to text span
    baseStyles = {
      fontSize: prototypeElement?.style?.fontSize, //prosemirror
      color: prototypeElement?.style?.color, //prosemirror
      fontWeight: prototypeElement?.style?.fontWeight, //prosemirror
      fontStyle: prototypeElement?.style?.fontStyle, //prosemirror
      textDecoration: prototypeElement?.style?.textDecoration, //prosemirror
      textAlign: prototypeElement?.style?.align,
      display: 'flex',
      lineHeight: prototypeElement?.style?.lineHeight ? prototypeElement?.style?.lineHeight : 'normal',
      justifyContent: prototypeElement?.style?.justifyContent !== '' ? prototypeElement?.style?.justifyContent : prototypeElement?.style?.align,
      alignItems: prototypeElement?.style?.valign,
      height: px(prototypeElement?.style?.containerHeight || prototypeElement?.style?.imageHeight, '100%'),
      width: px(prototypeElement?.style?.containerWidth || prototypeElement?.style?.imageWidth, '100%')
    }
  }

  overrideStyles = {
    textAlign: element?.style?.align || baseStyles.textAlign,
    display: 'flex',
    lineHeight: element?.style?.lineHeight || baseStyles.lineHeight || 'normal',
    justifyContent: element?.style?.justifyContent || baseStyles.justifyContent || element?.style?.align,
    alignItems: element?.style?.valign || baseStyles.alignItems,
    height: px(element?.style?.containerHeight || element?.style?.imageHeight, '100%') || baseStyles.height,
    width: px(element?.style?.containerWidth || element?.style?.imageWidth, '100%') || baseStyles.width
  }

  const finalStyles = { ...baseStyles, ...overrideStyles }

  if (element.ruleIds && element.type !== 'table') {
    const results = applyRules(element, rules.value);
    return { ...finalStyles, ...results }
  }

  element.alternateColor = null

  return finalStyles
};

const cssClasses = ref('element-content dragdrop')

const {savePresentation} = useMicroservice()

const reactiveJson = ref(null)
const elementRef = ref<HTMLElement | null>(null)

const mainStore = useMainStore()
const {editModeOn, toolbarState, undoRedoEvent, recalculate, updated} = storeToRefs(mainStore)
const widgetStore = useWidgetStore()
const { selectedWidgets, selectedWidget, selectedWidgetGroupId, multiSelect, selectedSectionId,
  selectedSectionRowId, selectedSubsectionId, selectedSubsectionRowId, sectionSelected, subsectionSelected, activeType } = storeToRefs(widgetStore)
const slidesStore = useSlidesStore()
const { rules } = storeToRefs(slidesStore)
const chosenLayout = ref([])

const eventDetected = ref(false)
let dragEvent: any = null
let eventProcessing = false
let editMode = true
const disableDrag = ref(false)

const shadow = computed(() => props.elementInfo.shadow)
const {shadowStyle} = useElementShadow(shadow)

const hideEmptyDropzone = computed(() => editModeOn.value ?'visible': 'hidden')

const widgetClass = computed(() => selectedWidget.value.widgetClass || '')

const componentTypes = {
  'model_data': markRaw(ModelData),
  'reg_text': markRaw(RegularTextWidget),
  'csv_selector': markRaw(CsvSelectorWidget),
  'image': markRaw(ImageWidget),
  'chart': markRaw(ChartWidget),
  'table': markRaw(TableWidget),
  'button': markRaw(ButtonWidget)
}

onBeforeMount(async () => {
  const findDragDrop: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByTypeAllSlides('dragdrop').find((zone) => zone.id === props.elementInfo?.id)
  reactiveJson.value = findDragDrop ? findDragDrop.widgets : []
})

onMounted(() => {
  window.addEventListener('dragFromToolbox', (event) => {
    eventDetected.value = !eventDetected.value
    dragEvent = event
  })

  chosenLayout.value = props.elementInfo.layout

  window.addEventListener('editMode', (event) => {
    editMode = event.detail.editMode
    disableDrag.value = !editMode
  })
})

const dragDropElement = computed(() => {
  return <PPTDragDropElement>slidesStore.getElementsByTypeAllSlides('dragdrop').find((zone) => zone.id === props.elementInfo?.id)
})

watch(undoRedoEvent, (val) => {
  if (val) {
    const findDragDrop = dragDropElement.value
    reactiveJson.value = findDragDrop ? findDragDrop.widgets : []

  }
})

watch(updated, (val) => {
  if (val) {
    const findDragDrop: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByTypeAllSlides('dragdrop').find((zone) => zone.id === props.elementInfo?.id)
    reactiveJson.value = findDragDrop ? findDragDrop.widgets : []
  }
})



watch(widgetClass, (newVal, oldVal) => {
  const tile = document.querySelector('.item-container');
  if (tile) {
    if (oldVal) {
      tile.classList.remove(`${oldVal}`);
    }

    if (newVal !== '') {
      tile.classList.add(`${newVal}`);
    }
  }
});

watch(() => props.elementInfo.layout, (newLayout) => {
  chosenLayout.value = newLayout;
}, { deep: true });

watch(eventDetected, () => {
  if (!eventProcessing) {
    dropToolboxWidget(dragEvent)
  }
})

const debouncedSave = _.debounce(async () => {
  await api.savePresentation();
}, 1000);

watch(reactiveJson, async (newVal) => {
  slidesStore.updateElement({id: props.elementInfo.id, props: {widgets: newVal}})
  if (!undoRedoEvent.value) {
    addHistorySnapshot()
  }

  await savePresentation()
}, {deep: true})

const selectWidgetElement = (event, element: DraggableElement, widgetGroupId) => {
  if (multiSelect.value) {
    widgetStore.addActiveWidget(element)
  }
  else {
    widgetStore.setActiveWidget(element, widgetGroupId, props.sectionId, props.sectionRowId)
    widgetStore.setActiveDragDrop(props.elementInfo.id)

    sectionSelected.value = false
    subsectionSelected.value = false
  }
}

const setActiveSubsection = (subsectionRowId, subsectionId) => {
  selectedSectionRowId.value = props.sectionRowId
  selectedSectionId.value = props.sectionId
  selectedSubsectionRowId.value = subsectionRowId
  selectedSubsectionId.value = subsectionId
}

const getDropZone = ((parentId) => {
  try {
    // can't do equals here because we used to also have sublayouts that no longer exist
    const parentExists = reactiveJson.value.filter(item => item.parent.indexOf(parentId) === 0)
    if (parentExists.length === 0) {
      reactiveJson.value = [...reactiveJson.value, {
        'parent': parentId,
        'children': []
      }]
    }

    return replaceTextWithComponents(parentExists ? parentExists.flatMap(obj => obj.children) : []);
  } catch(e) {
    console.log('catch', e)
  }
})

const replaceTextWithComponents = ((children) => {
  const formattedChildren = []
  children.forEach((item) => {
    const component = componentTypes[item.type]
    formattedChildren.push({
      data: item,
      component
    })
  })

  return formattedChildren
})

const findParent = (id) => {
  for (const dragdrop of slidesStore.getElementsByType('dragdrop')) {
    for (const widget of dragdrop.widgets) {
      if (widget.parent === id) {
        return widget
      }
    }
  }
  return null
}

const moveWidget = ((event) => {
  const {to, from, oldIndex, newIndex} = event

  if (to === from && oldIndex === newIndex) return

  const source = findParent(from.id)
  const target = findParent(to.id)

  if (!source || !target) return

  const [widgetToMove] = source.children.splice(oldIndex, 1)
  target.children.splice(newIndex, 0, widgetToMove)
  updated.value = Date.now()
})

const dropToolboxWidget = (async (event) => {
  if (!eventProcessing) {
    eventProcessing = true
    event.stopImmediatePropagation()
    const {target, component, elementId, index} = event.detail
    if (target) await addDraggableElement(target, component, elementId, index)
    eventProcessing = false
  }
})

const addDraggableElement = (target, component, elementId, index) => {
  return new Promise((resolve) => {

    const targetEl: {
      parent: string;
      children: DraggableElement[]
    } | undefined = reactiveJson.value.find((item) => item.parent.indexOf(target) === 0)

    let newDraggableElement: DraggableElement = {
      id: elementId,
      type: component,
      subType: 'out_text',
      hookId: '',
      compact: false,
      iconPosition: 'none',
      text: component === 'reg_text'? 'Here is your new text block.': 'Select data from Value Model',
      step: 1,
      currency: 'USD',
      locale: 'en-US',
      csv: {},
      style: {},
      chartType: component === 'chart' ? 'pie' : '',
      imageUrl: '',
    }

    if (component === 'chart') {
      const chart = createChartWidget(component)
      newDraggableElement.style['containerHeight'] = 500
      newDraggableElement.style['containerWidth'] = 600
      newDraggableElement = { ...newDraggableElement, ...chart }
    }

    if (component === 'table') {
      const table = useCreateElement().createTableElement(2, 5)
      newDraggableElement = { ...newDraggableElement, ...table, id: newDraggableElement.id }
      delete table.id;
    }

    if (targetEl) {
      targetEl.children.splice(index, 0, newDraggableElement)
      widgetStore.setActiveWidget(newDraggableElement)
    }
    resolve()
  })
}

const createChartWidget = (type: string) => {
  const newElement = {
    left: 300,
    top: 81.25,
    width: 400,
    height: 400,
    rotate: 0,
    themeColor: ['#5b9bd5'],
    gridColor: 'rgb(230, 230, 230)',
    data: {
      labels: [],
      legends: [],
      series: [],
      hookIds: []
    },
    labelStyle: {
      fontSize: '12px'
    },
  }

  let options: ChartOptions = {}
  if (type === 'horizontalBar') options = { horizontalBars: true }
  else if (type === 'area') options = { showArea: true }
  else if (type === 'scatter') options = { showLine: false }
  else if (type === 'ring') options = { donut: true }

  return {
    ...newElement,
    options,
  }
}

const determineMoveCapability = ((event) => {
  const rect = elementRef.value.getBoundingClientRect()
  const borderWidth = 10

  const isOverBorder =
      event.clientX < rect.left + borderWidth ||
      event.clientX > rect.right - borderWidth ||
      event.clientY < rect.top + borderWidth ||
      event.clientY > rect.bottom - borderWidth

  handleSelectElement(event, isOverBorder)
})


const borderUpdate = computed(() => {
  return (activeType.value !== 'none' || subsectionSelected.value || sectionSelected.value) ? '3px solid rgba(255, 165, 0, 0.5)' : 'none';
});

const activeBorderUpdate = computed(() => {
  return (activeType.value !== 'none' || subsectionSelected.value || sectionSelected.value) ? '3px solid #2196F3' : 'none';
});

const handleSelectElement = (e: MouseEvent | TouchEvent, canMove) => {
  if (props.elementInfo.lock) return
  e.stopPropagation()
  props.selectElement(e, props.elementInfo, canMove)
}
</script>

<style lang="scss" scoped>
.grey-background {
  background-color: #DFDFDF;
  visibility: v-bind(hideEmptyDropzone);
  padding: 15px 40px 15px 40px;
}

.grey-background::before {
  margin-left: auto;
  margin-right: auto;
  margin-top: 5px;
  font-size: 1rem;
  content: "Drag a Tile Here";
  color: #666666;
  display: block;
}

.draggable-row {
  --bs-gutter-x: 0;
  --bs-gutter-y: 0;
}

.element-content.dragdrop.active-dropzone {
  border: dashed 5px rgb(53, 150, 243);
  position: absolute;
}

.widget-group {

  min-height: 30px;

  color-scheme: light;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;
}

.widget-holder {
  position: relative;
  display: inline-block;
  justify-content: center;
  align-items: center;
  background-color: transparent;
}

.close {
  float: right;
}

.close:hover {
  cursor: pointer;
}

.editable-element-drag-drop {

  &.lock .element-content {
    cursor: default;
  }
}

.rotate-wrapper {
  width: 100%;
  height: 100%;
}


.drag-handler {
  height: 10px;
  position: absolute;
  left: 0;
  right: 0;

  &.top {
    top: 0;
  }

  &.bottom {
    bottom: 0;
  }
}

.item-container {
  border-radius: 10px;
  border: solid transparent 1px;
  justify-content: center;
  display: flex;
  flex-direction: row;
  position: relative;
}

.item-container:hover {
  border: solid #dee2e6 1px;
}

.focus-style {
  outline: 3px solid #2196F3 !important;
}

/*hover highlight is different if it's a prototype tile*/
.prototype-tile:hover {
  outline: 3px solid #3aff35 !important;
}

.subsection {
  outline: v-bind(borderUpdate)
}

.subsection.active {
  outline: v-bind(activeBorderUpdate);
}

:deep(.focus-style .show-editor .prosemirror-editor) {
  width: 100%;
  border: solid 1px #2a2a2a40;
  border-radius: 4px;
}

:deep(.focus-style .active-editor) {
  border: solid 1px #2a2a2a40;
  border-radius: 4px;
}

:deep(.focus-style .active) {
  border: solid 3px #2196F3 !important;
}

</style>
